"use client";
import { Fragment, useState } from "react";
import { Trash2 } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import LoadingAll from "@/components/LoadingAll";
import LoadError from "@/components/LoadError";
import CartItem from "@/components/pages/Cart/_CartItem";
import MobileFixedFooter from "@/components/pages/Cart/_mobileFixedFooter";
import CartSummary from "@/components/pages/Cart/_CartSummary";
import FrequentlyBoughtTogether from "@/components/FrequentlyBoughtTogether";
import Button from "@/components/ui/button";
import Tabs from "@/components/ui/Tabs";
import useClearCart from "@/components/pages/Cart/_useClearCart";
import Image from "next/image";


const Index = () => {
	const { cart, user_loading, user_error } = useAuthContext();
	const { web_route_names } = useWebSiteContext();
	const [activeTab, setActiveTab] = useState(0);
	// حذف کل سبد در `_useClearCart` انجام می‌شود؛ آن هوک بعد از موتیشن حذف
	// کلی، سبد برگشتی را بازبینی می‌کند تا سبد فقط در UI خالی نشود.
	const { clear_cart, clearing: clearing_cart } = useClearCart();

	const hasProducts = cart?.products?.length > 0;
	const productsCount = cart?.products?.length || 0;

	if (user_error) return <LoadError />;

	return (
		user_loading ? <LoadingAll /> :
			<>
				<div className="block lg:hidden">

					{/* title & delete
					    در موبایل تب لازم نیست؛ تعداد آیتم‌های سبد کنار همین تایتل
					    نمایش داده می‌شود. */}
					<div className="w-full flex items-center justify-between py-4 px-4 md:px-10 ">
						<h1 className="flex items-center gap-1 text-sm text-jmt-main font-bold">
							<span> سبد خرید شما </span>
							{productsCount > 0 && (
								<span className="flex items-center justify-center w-5 h-5 text-xs text-jmt-white bg-jmt-primary-700 border border-jmt-neutral-100 rounded-md">
									{productsCount}
								</span>
							)}
						</h1>

						{hasProducts && (
							<Button
								start_icon={<Trash2 size={16} />}
								color_type="primary"
								variant="primary"
								text="حذف همه"
								className="text-xs border-none p-0!"
								loading={clearing_cart}
								onClick={clear_cart}
							/>
						)}
					</div>

					{/* جداکننده‌ها فقط وقتی محصولی در سبد هست معنا دارند؛ در حالت
					    خالی بودن سبد نباید زیر پیام «سبد خالی» بردر بیفتد. */}
					{hasProducts && <div className="w-full h-0.5 bg-jmt-neutral-200" />}

					{/* cart items */}
					<div className="w-full px-4 md:px-10">
						{hasProducts ?
							cart?.products?.map((item, index) => (
								<Fragment key={item?.id ?? `cart-item-${index}`}>
									<CartItem index={index} item={item} />
									{!(index === cart?.products?.length - 1) && <hr className="text-jmt-neutral-200" />}
								</Fragment>
							))
							:
							<div className="flex flex-col items-center justify-center space-y-4 my-[70px]">
								<div className="w-[110px] h-[110px]">
									<Image
										src="/images/empty-states/cart.png"
										alt=" سبد شما خالی است"
										className="object-contain w-full h-full"
										width={110}
										height={110}
									/>
								</div>
								<div className="flex flex-col justify-center items-center text-sm text-jmt-main text-center">
									<span className="text-center"> سبدت که هنوز خالیه </span>
									<span className="text-center">وقتشه بین کلی برند ایرانی، سلیقتو به رخمون بکشی!</span>
								</div>
								<Button
									href={web_route_names?.["search"]}
									text="مشاهده محصولات"
									color_type="primary"
								/>
							</div>
						}
					</div>

					{hasProducts && <div className="w-full h-0.5 bg-jmt-neutral-200" />}

					{hasProducts && <CartSummary cart={cart} />}

					<div className="w-full px-4 md:px-10">
						{hasProducts && <FrequentlyBoughtTogether className="pb-4 lg:p-0" topSeparator />}
						<MobileFixedFooter cart={cart} />
					</div>
				</div >

				<div className="hidden lg:block">
					<div className="container">
						<div className={`flex justify-between pb-4 mb-8 gap-4 ${hasProducts ? "border-b-2 border-jmt-neutral-200" : ""}`}>
							<main className={`${hasProducts ? "w-[calc(100%-400px)]" : "w-full"} h-fit`}>

								{/* header tabs - تب گلوبال پروژه
								    قابلیت «سبد خرید بعدی» فعلا غیرفعال است، پس فعلا فقط یک
								    آیتم دارد. */}
								<Tabs
									items={[
										{
											id: 0,
											label: "سبد خرید",
											badge: (
												<span className={`flex items-center justify-center w-5 h-5 text-xs text-jmt-white border border-jmt-neutral-100 rounded-md ${activeTab === 0 ? "bg-jmt-primary-700 " : "bg-jmt-neutral-500"}`}>
													{productsCount}
												</span>
											)
										}
										// { id: 1, label: "سبد خرید بعدی", badge: ... }
									]}
									value={activeTab}
									onChange={setActiveTab}
									className="mb-4"
									tabClassName="py-2 px-4"
									endContent={activeTab === 0 && hasProducts ? (
										<Button
											start_icon={<Trash2 size={16} />}
											color_type="primary"
											variant="primary"
											text="حذف همه"
											className="text-xs border-none py-2!"
											onClick={clear_cart}
											loading={clearing_cart}
										/>
									) : null}
								/>

								{/* tab content */}
								{activeTab === 0 && (
									hasProducts ?
										cart?.products?.map((item, index) => (
											<Fragment key={item?.id ?? `cart-item-desktop-${index}`}>
												<CartItem index={index} item={item} variant="cart" />
												{!(index === cart?.products?.length - 1) && <hr className="text-jmt-neutral-200 my-1" />}
											</Fragment>
										))
										: <div className="flex flex-col items-center justify-center space-y-4 my-[70px]">
											<div className="w-[130px] h-[130px]">
												<Image
													src="/images/empty-states/cart.png"
													alt=" سبد شما خالی است"
													className="object-contain w-full h-full"
													width={130}
													height={130}
												/>
											</div>


											<div className="flex flex-col justify-center items-center text-sm text-jmt-main text-center">
												<span className="text-center"> سبدت که هنوز خالیه </span>
												<span className="text-center">وقتشه بین کلی برند ایرانی، سلیقتو به رخمون بکشی!</span>
											</div>
											<Button
												text="مشاهده محصولات"
												href={web_route_names?.["search"]}
												color_type="primary"
											/>
										</div>
								)}

								{/* قابلیت «سبد خرید بعدی» فعلا غیرفعال است
								{activeTab === 1 && (
									<div className="flex flex-col items-center justify-center space-y-4 my-[70px]">
										<div className="w-[110px] h-[110px] lg:w-[130px] lg:h-[130px]">
											<Image
												src="/images/empty-states/buy-later.png"
												alt="لوگو ژومنت"
												className="object-contain w-full h-full"
												width={130}
												height={130}
											/>
										</div>
										<div className="flex flex-col justify-center items-center text-sm text-jmt-main text-center">
											<span className="text-center">هنوز چیزی برای بعد ذخیره نکردی  </span>
											<span className="text-center"> آیتم‌هایی که بعداً می‌خوای بخری رو اینجا نگه دار </span>
										</div>
										<Button
											text="مشاهده محصولات"
											href={web_route_names?.["search"]}
											color_type="primary"
										/>
									</div>
								)}
								*/}
							</main>
							{activeTab === 0 && hasProducts && <CartSummary cart={cart} />}
						</div>

						{hasProducts && <FrequentlyBoughtTogether />}
					</div>
				</div>
			</>
	)
}
export default Index;
